import { createListenerMiddleware } from '@reduxjs/toolkit'
import type { RootState } from '@/store/store'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { getTrackEventGlobal } from '@/analytics/utils/analytics-helpers'
import { trackTableDownloaded } from '@/analytics/utils/table-analytics'
import { ExportActions } from './slice'
import { tableExportRequest } from '../../fetch/table-export'
import type { TableExportQuery } from '../schemas'

function recordPreparedTableDownload(tableId: string, query: TableExportQuery) {
  const track = getTrackEventGlobal()
  if (!track) return
  trackTableDownloaded(track, tableId, query.filters.length > 0 || Boolean(query.search?.trim()))
}

export const tableExportListenerMiddleware = createListenerMiddleware()
tableExportListenerMiddleware.startListening({
  actionCreator: ExportActions.start,
  effect: async ({ payload }, api) => {
    if ((api.getOriginalState() as RootState).tableExports.byTable[payload.tableId]?.busy) return
    const controller = new AbortController()
    const signal = AbortSignal.any([api.signal, controller.signal])
    let expiryTimer: ReturnType<typeof setTimeout> | undefined
    const expiredMessage = 'The table export expired. Please start a new download.'
    try {
      let job = await tableExportRequest(payload.tableId, payload.query, signal)
      const expiresIn = Date.parse(job.expires_at) - Date.now()
      if (expiresIn <= 0) throw new Error(expiredMessage)
      expiryTimer = setTimeout(() => controller.abort(new Error(expiredMessage)), expiresIn)
      while (job.status === 'pending' || job.status === 'running') {
        api.dispatch(ExportActions.received({ tableId: payload.tableId, job }))
        await api.delay(Math.min(2000, Math.max(0, Date.parse(job.expires_at) - Date.now())))
        if (signal.aborted || Date.now() >= Date.parse(job.expires_at)) throw new Error(expiredMessage)
        job = await tableExportRequest(payload.tableId, job.id, signal)
      }
      if (signal.aborted) throw new Error(expiredMessage)
      if (job.status === 'failed') throw new Error(job.error || 'Unable to prepare this download. Please try again.')
      if (job.status === 'completed') {
        if (!job.download_url) throw new Error('The prepared download is unavailable. Please try again.')
        const link = document.createElement('a')
        link.href = job.download_url
        link.download = `table.${payload.query.format ?? 'xlsx'}`
        document.body.appendChild(link)
        try {
          link.click()
        } finally {
          link.remove()
        }
        recordPreparedTableDownload(payload.tableId, payload.query)
      }
      api.dispatch(ExportActions.received({ tableId: payload.tableId, job }))
    } catch (error) {
      if (!api.signal.aborted) {
        const message = controller.signal.aborted ? expiredMessage : error instanceof Error ? error.message : 'Unable to prepare this download.'
        api.dispatch(ExportActions.failed({ tableId: payload.tableId, error: message }))
        api.dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message, durationMs: kToastDurationMediumMs }))
      }
    } finally {
      clearTimeout(expiryTimer)
    }
  },
})
